import { AvailabilitySearchQuery, LockOptions } from 'types';
import { Button } from 'omakase';
import { ExperienceCardAction } from 'utils/searchBar';
import { LocalDateTime } from 'js-joda';
import { OfferingDetails } from 'components/business/offering/OfferingDetails';
import { SearchTicketGroupWithSeatingAreas } from 'components/www/availability-search/AvailabilitySearchResultSimple';
import { consumer, core, safeToNumber } from '@tock/coretypes';
import { defaultMemoize } from 'reselect';
import { getClosestTicketGroups } from 'utils/citySearchUtils';
import {
  isOfferingEventType,
  isOnlineOrdersType,
  shouldRenderCTAFromActionAndQuery,
} from 'utils/offerings';
import React, { FC } from 'react';
import TimeAndSizeItem from 'components/www/availability-search/TimeAndSizeItem';

interface ExperienceResultProps {
  business: consumer.OfferingBusiness;
  lockedDateTime?: string;
  lockedSeatingArea?: core.SeatingArea;
  locale: string;
  offering: consumer.Offering;
  onLockTimeAndSize(
    ticketTypeId: number,
    selectedSize: number,
    dateTime: LocalDateTime,
    priceCents: number,
    options: LockOptions,
    ticketTypeName: string
  ): void;
  onNavigateToExperience(business: consumer.OfferingBusiness, experience: consumer.Offering): void;
  onNavigateToExperienceMenu(
    business: consumer.OfferingBusiness,
    experience: consumer.Offering
  ): void;
  query: AvailabilitySearchQuery;
  ticketGroup: SearchTicketGroupWithSeatingAreas[];
  timeZone: string;
  experienceCardAction: ExperienceCardAction;
}

const RESULTS_LIMIT = 5;

export const ExperienceResult: FC<ExperienceResultProps> = ({
  business,
  lockedDateTime,
  lockedSeatingArea,
  locale,
  offering,
  onLockTimeAndSize,
  onNavigateToExperience,
  onNavigateToExperienceMenu,
  query,
  ticketGroup,
  timeZone,
  experienceCardAction,
}) => {
  const generateAvailabilities = defaultMemoize(
    (ticketGroups: SearchTicketGroupWithSeatingAreas[]) => {
      const { date, time } = query;
      return getClosestTicketGroups(ticketGroups, {
        timeSearched: time,
        dateSearched: date,
        limit: RESULTS_LIMIT,
      });
    }
  );

  const handleSelectTimeAndSize = (
    dateTime: LocalDateTime,
    priceCents: number,
    partySize: number,
    lockOptions?: Partial<LockOptions>
  ) => {
    const ticketTypeId = offering ? offering.id : -1; //TODO handle error
    const options = {
      ...(lockOptions || {}),
      showError: false,
    };
    onLockTimeAndSize(ticketTypeId, partySize, dateTime, priceCents, options, offering.name);
  };

  const handleNavigateToExperience = () => {
    if (onNavigateToExperience) {
      onNavigateToExperience(business, offering);
    }
  };

  const handleNavigateToExperienceMenu = (e: React.MouseEvent) => {
    e.preventDefault();

    if (onNavigateToExperienceMenu) {
      onNavigateToExperienceMenu(business, offering);
    }
  };

  const renderClosestAvailableTicketGroups = (
    closestAvailableTicketGroups: SearchTicketGroupWithSeatingAreas[]
  ): React.ReactNode => {
    const { size } = query;

    // If party size isn't set, a "Book for XX date" button should be displayed,
    // instead of available ticket groups.
    const partySize = size;
    if (!partySize) {
      return undefined;
    }

    const hasMoreTimes = ticketGroup.length > RESULTS_LIMIT;
    return (
      <>
        {closestAvailableTicketGroups.map(
          (availableTicketGroup: SearchTicketGroupWithSeatingAreas, idx: number) => {
            return (
              <TimeAndSizeItem
                key={idx}
                locale={locale}
                lockedDateTime={lockedDateTime}
                lockedSeatingArea={lockedSeatingArea}
                onClick={handleSelectTimeAndSize}
                partySize={partySize}
                price={safeToNumber(availableTicketGroup.price)}
                minRequiredSpend={{
                  totalCents: safeToNumber(availableTicketGroup.minRequiredSpend?.totalCents),
                  priceDisplayDistribution:
                    availableTicketGroup.minRequiredSpend?.distribution ?? undefined,
                }}
                priceDisplayStyle={offering.price?.priceDisplayStyle ?? 'BASE_TICKET_PRICE'}
                time={availableTicketGroup.availableDateTime}
                timeZone={timeZone}
                offering={offering}
                business={business}
                seatingAreas={availableTicketGroup.seatingAreas}
              />
            );
          }
        )}
        {hasMoreTimes && (
          <div className="AvailabilitySearch-experienceSeeAllTimesContainer">
            <Button
              onClick={handleNavigateToExperience}
              aria-label="See all times (opens a dialog)"
              className="AvailabilitySearch-experienceSeeAllTimes"
              size="large"
              variant="basic"
            >
              See all times
            </Button>
          </div>
        )}
      </>
    );
  };

  const getCallToActionType = (
    closestAvailableTicketGroups: consumer.SearchTicketGroup[]
  ): 'CTA' | 'TIMES' => {
    const type =
      isOfferingEventType(offering) && closestAvailableTicketGroups.length > 0
        ? 'CTA'
        : isOnlineOrdersType(offering)
        ? 'CTA'
        : shouldRenderCTAFromActionAndQuery(experienceCardAction, query)
        ? 'CTA'
        : 'TIMES';
    return type;
  };

  const renderCallToAction = (closestAvailableTicketGroups: consumer.SearchTicketGroup[]) => {
    return isOfferingEventType(offering) && closestAvailableTicketGroups.length > 0 ? (
      <Button
        variant="primary"
        size="small"
        aria-label={`Get tickets for ${offering.name} (opens a dialog)`}
        onClick={handleNavigateToExperience}
      >
        Get tickets
      </Button>
    ) : isOnlineOrdersType(offering) ? (
      <>
        <Button
          variant="primary"
          size="small"
          aria-label={`Order now (opens a dialog)`}
          onClick={handleNavigateToExperience}
        >
          Order now
        </Button>
        {offering.showMenuLink && (
          <Button
            className="Consumer-menuLink"
            aria-label={`View menu for ${offering.name} (opens a dialog)`}
            size="small"
            variant="ghost"
            onClick={handleNavigateToExperienceMenu}
            data-testid="view-menu-button"
          >
            View menu
          </Button>
        )}
      </>
    ) : shouldRenderCTAFromActionAndQuery(experienceCardAction, query) ? (
      <Button
        onClick={handleNavigateToExperience}
        variant="primary"
        size="small"
        aria-label={`Book now (opens a dialog)`}
      >
        Book now
      </Button>
    ) : null;
  };

  const closestAvailableTicketGroups = generateAvailabilities(
    ticketGroup
  ) as SearchTicketGroupWithSeatingAreas[];
  const ctaType = getCallToActionType(closestAvailableTicketGroups);
  return (
    <li className="AvailabilitySearch-experience">
      <OfferingDetails
        business={business}
        offering={offering}
        onNavigateToExperience={handleNavigateToExperience}
        descriptionCutoffLength={140}
      />
      {ctaType === 'CTA' && (
        <div className="AvailabilitySearch-callToAction">
          {renderCallToAction(closestAvailableTicketGroups)}
        </div>
      )}
      {ctaType === 'TIMES' && (
        <div className="AvailabilitySearch-experienceTimes">
          {renderClosestAvailableTicketGroups(closestAvailableTicketGroups)}
        </div>
      )}
    </li>
  );
};
